<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("PRODUCTS.PRODUCTS_LIST") }}</h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openProductCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_PRODUCTS)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">{{
                $t("PRODUCTS.ADD_PRODUCT")
              }}</span>
            </base-button>
            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_PRODUCTS)"
              :objectType="'products'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <product-list-table
        @onViewProduct="openProductViewModal"
        @onEditProduct="openProductEditModal"
        @onDeleteProduct="deleteProduct"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewProductModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewProductModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'PRODUCT_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("PRODUCTS.VIEW_PRODUCT") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openProduct"
                :objectType="'products'"
                :objectId="openProduct.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button class="edit" @click="openProductEditModal(openProduct)">
                  <i class="fal fa-edit"></i>
                  <span>{{ $t("COMMON.EDIT") }}</span>
                </button>
                <button class="edit" @click="deleteProduct(openProduct)">
                  <i class="fal fa-trash-alt"></i>
                  <span>{{ $t("COMMON.DELETE") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeProductModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-product-page v-if="openProduct" :productId="openProduct.id" />
          </div>
        </div>
      </div>

      <div
        v-if="isEditProductModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditProductModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'PRODUCT'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("PRODUCTS.EDIT_PRODUCT") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button class="view" @click="openProductViewModal(openProduct)">
                  <i class="fal fa-eye"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeProductModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-product-page
              v-if="openProduct"
              :productId="openProduct.id"
              @onViewProduct="openProductViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddProductModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddProductModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'PRODUCT'">
          <div class="resizable-wrapper-content-header">
            <h1>
              {{ $t("PRODUCTS.ADD_PRODUCT") }}
            </h1>
            <button class="close" @click="closeProductModal">
              <i class="fal fa-times"></i>
            </button>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-product-page @onViewProduct="openProductViewModal" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import ProductListTable from "./partials/ProductListTable.vue";
import EditProductPage from "./components/EditProductComponent.vue";
import AddProductPage from "./components/AddProductComponent.vue";
import ViewProductPage from "./components/ViewProductComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    ProductListTable,
    NotificationSubscription,
    EditProductPage,
    AddProductPage,
    ViewProductPage,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  computed: {},

  watch: {},

  data() {
    const productId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewProductModalOpened = false;
    let isEditProductModalOpened = false;
    let isAddProductModalOpened = false;
    let openProduct = null;
    if (productId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewProductModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditProductModalOpened = true;
      }
      openProduct = { id: productId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddProductModalOpened = true;
    }
    return {
      isViewProductModalOpened: isViewProductModalOpened,
      isEditProductModalOpened: isEditProductModalOpened,
      isAddProductModalOpened: isAddProductModalOpened,
      openProduct: openProduct,
      renderKey: 1,
    };
  },

  methods: {
    openProductCreateModal() {
      this.closeProductModal();
      this.isAddProductModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Products",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openProductViewModal(product, reRender = false) {
      this.closeProductModal();
      this.openProduct = product;
      this.isViewProductModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Products",
          query: { id: this.openProduct.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },
    openProductEditModal(product) {
      this.closeProductModal();
      this.openProduct = product;
      this.isEditProductModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Products",
          query: { id: this.openProduct.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    closeProductModal() {
      this.isAddProductModalOpened = false;
      this.isViewProductModalOpened = false;
      this.isEditProductModalOpened = false;
      this.openProduct = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List Products",
          query: {},
        }).href
      );
    },

    async deleteProduct(product) {
      const confirmation = await swal.fire({
        title: this.$t("PRODUCTS.DELETE_THIS_PRODUCT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("products/destroy", product.id);
          this.renderKey++;
          this.closeProductModal();
          this.$notify({
            type: "success",
            message: this.$t("PRODUCTS.PRODUCT_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
